import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AgentManagement from '../Admin/AgentManagement';
import { notificationService } from '../../services/notificationService';
import { authFetch } from '../../utils/authFetch';

const SettingsView: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState(notificationService.getSettings());
  const [desktopPermission, setDesktopPermission] = useState<NotificationPermission>('default');
  const [whatsappConfig, setWhatsappConfig] = useState<any>(null);
  const [editingWhatsApp, setEditingWhatsApp] = useState(false);
  const [whatsappForm, setWhatsappForm] = useState({
    apiToken: '',
    phoneNumberId: '',
    businessAccountId: '',
    webhookVerifyToken: '',
    apiUrl: 'https://graph.facebook.com/v18.0'
  });
  const [savingWhatsApp, setSavingWhatsApp] = useState(false);
  const [agentSettings, setAgentSettings] = useState<any>(null);
  const [editingAgentSettings, setEditingAgentSettings] = useState(false);
  const [agentSettingsForm, setAgentSettingsForm] = useState({
    autoAssignEnabled: false,
    workingHoursStart: '09:00',
    workingHoursEnd: '18:00',
    responseTimeTarget: 5
  });

  useEffect(() => {
    // Check desktop notification permission
    setDesktopPermission(notificationService.getPermissionStatus());
    
    // Load WhatsApp configuration
    loadWhatsAppConfig();
    loadAgentSettings();
  }, []);

  const loadWhatsAppConfig = async () => {
    try {
      // authFetch automatically prepends /api
      const response = await authFetch('/settings/whatsapp');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setWhatsappConfig(data.data);
          // Pre-fill form with existing values if available
          if (data.data.phoneNumberId) {
            setWhatsappForm({
              apiToken: '', // Don't show existing token for security
              phoneNumberId: data.data.phoneNumberId || '',
              businessAccountId: data.data.businessAccountId || '',
              webhookVerifyToken: '', // Don't show existing token
              apiUrl: data.data.apiUrl || 'https://graph.facebook.com/v18.0'
            });
          }
        }
      }
    } catch (error) {
      console.error('Error loading WhatsApp config:', error);
    }
  };

  const saveWhatsAppConfig = async () => {
    if (!whatsappForm.apiToken || !whatsappForm.phoneNumberId || !whatsappForm.webhookVerifyToken) {
      alert('Please fill in all required fields (API Token, Phone Number ID, and Webhook Verify Token)');
      return;
    }

    setSavingWhatsApp(true);
    try {
      const response = await authFetch('/settings/whatsapp', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(whatsappForm)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('WhatsApp configuration saved successfully!');
          setEditingWhatsApp(false);
          loadWhatsAppConfig(); // Reload to get updated business name
        } else {
          alert('Failed to save configuration');
        }
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving WhatsApp config:', error);
      alert('Failed to save configuration');
    } finally {
      setSavingWhatsApp(false);
    }
  };

  const loadAgentSettings = async () => {
    try {
      // authFetch automatically prepends /api
      const response = await authFetch('/settings/agent');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAgentSettings(data.data);
          setAgentSettingsForm(data.data);
        }
      }
    } catch (error) {
      console.error('Error loading agent settings:', error);
    }
  };

  const saveAgentSettings = async () => {
    try {
      // authFetch automatically prepends /api
      const response = await authFetch('/settings/agent', {
        method: 'PUT',
        body: JSON.stringify(agentSettingsForm)
      });
      if (response.ok) {
        setAgentSettings(agentSettingsForm);
        setEditingAgentSettings(false);
        alert('Agent settings saved successfully!');
      }
    } catch (error) {
      console.error('Error saving agent settings:', error);
      alert('Failed to save agent settings');
    }
  };

  const toggleNotification = async (key: keyof typeof notifications) => {
    if (key === 'desktopNotifications' && desktopPermission !== 'granted') {
      const permission = await notificationService.requestPermission();
      setDesktopPermission(permission);
      if (permission === 'granted') {
        notificationService.updateSetting('desktopNotifications', true);
        setNotifications(notificationService.getSettings());
      }
      return;
    }
    
    const newValue = !notifications[key];
    notificationService.updateSetting(key, newValue);
    setNotifications(notificationService.getSettings());
    
    // Test notification when enabling
    if (key === 'soundEnabled' && newValue) {
      notificationService.testNotification();
    }
  };

  const testAllNotifications = () => {
    notificationService.testNotification();
    alert('Test notification sent! Check your browser and listen for sound.');
  };

  return (
    <div className="settings-view">
      {user?.role === 'ADMIN' && <AgentManagement />}
      
      <h2 className="settings-title">System Settings</h2>
      
      <div className="settings-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 className="settings-section-title" style={{ marginBottom: 0 }}>WhatsApp Integration</h3>
          {user?.role === 'ADMIN' && (
            <button
              onClick={() => {
                if (editingWhatsApp) {
                  loadWhatsAppConfig(); // Reset form
                }
                setEditingWhatsApp(!editingWhatsApp);
              }}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: editingWhatsApp ? '#6b7280' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {editingWhatsApp ? 'Cancel' : 'Edit'}
            </button>
          )}
        </div>

        {editingWhatsApp && user?.role === 'ADMIN' ? (
          <div>
            <div className="settings-item" style={{ marginBottom: '16px' }}>
              <span className="settings-label">API Token *</span>
              <input
                type="password"
                value={whatsappForm.apiToken}
                onChange={(e) => setWhatsappForm({...whatsappForm, apiToken: e.target.value})}
                className="form-input"
                placeholder="Enter WhatsApp API Token"
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
              />
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                Get this from Meta Business Manager → WhatsApp → API Setup
              </div>
            </div>

            <div className="settings-item" style={{ marginBottom: '16px' }}>
              <span className="settings-label">Phone Number ID *</span>
              <input
                type="text"
                value={whatsappForm.phoneNumberId}
                onChange={(e) => setWhatsappForm({...whatsappForm, phoneNumberId: e.target.value})}
                className="form-input"
                placeholder="Enter Phone Number ID"
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
              />
            </div>

            <div className="settings-item" style={{ marginBottom: '16px' }}>
              <span className="settings-label">Business Account ID</span>
              <input
                type="text"
                value={whatsappForm.businessAccountId}
                onChange={(e) => setWhatsappForm({...whatsappForm, businessAccountId: e.target.value})}
                className="form-input"
                placeholder="Enter Business Account ID (optional)"
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
              />
            </div>

            <div className="settings-item" style={{ marginBottom: '16px' }}>
              <span className="settings-label">Webhook Verify Token *</span>
              <input
                type="text"
                value={whatsappForm.webhookVerifyToken}
                onChange={(e) => setWhatsappForm({...whatsappForm, webhookVerifyToken: e.target.value})}
                className="form-input"
                placeholder="Enter Webhook Verify Token"
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
              />
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                Use this same token when configuring webhook in Meta Business Manager
              </div>
            </div>

            <div className="settings-item" style={{ marginBottom: '16px' }}>
              <span className="settings-label">API URL</span>
              <input
                type="text"
                value={whatsappForm.apiUrl}
                onChange={(e) => setWhatsappForm({...whatsappForm, apiUrl: e.target.value})}
                className="form-input"
                placeholder="https://graph.facebook.com/v18.0"
                style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
              />
            </div>

            {whatsappConfig?.webhookUrl && (
              <div className="settings-item" style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '8px' }}>
                <span className="settings-label" style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Webhook URL</span>
                <div style={{ fontSize: '12px', wordBreak: 'break-all', color: '#6b7280', fontFamily: 'monospace' }}>
                  {whatsappConfig.webhookUrl}
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '8px' }}>
                  Copy this URL and configure it in Meta Business Manager → WhatsApp → Configuration → Webhooks
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                onClick={saveWhatsAppConfig}
                className="assign-button"
                disabled={savingWhatsApp}
              >
                {savingWhatsApp ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="settings-item">
              <span className="settings-label">API Status</span>
              <span className="settings-value" style={{ color: whatsappConfig?.tokenConfigured ? '#10b981' : '#ef4444' }}>
                {whatsappConfig?.tokenConfigured ? '● Connected' : '● Not Connected'}
              </span>
            </div>
            {whatsappConfig?.displayName && (
              <div className="settings-item">
                <span className="settings-label">Business Name</span>
                <span className="settings-value" style={{ fontWeight: '500' }}>
                  {whatsappConfig.displayName}
                </span>
              </div>
            )}
            <div className="settings-item">
              <span className="settings-label">Phone Number</span>
              <span className="settings-value" style={{ fontWeight: '500', fontSize: '14px' }}>
                {whatsappConfig?.phoneNumber || 'Not configured'}
              </span>
            </div>
            <div className="settings-item">
              <span className="settings-label">Phone Number ID</span>
              <span className="settings-value" style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>
                {whatsappConfig?.phoneNumberId || 'Not configured'}
              </span>
            </div>
            <div className="settings-item">
              <span className="settings-label">Business Account ID</span>
              <span className="settings-value" style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>
                {whatsappConfig?.businessAccountId || 'Not configured'}
              </span>
            </div>
            <div className="settings-item">
              <span className="settings-label">API Version</span>
              <span className="settings-value">
                {whatsappConfig?.apiVersion || 'v18.0'}
              </span>
            </div>
            <div className="settings-item">
              <span className="settings-label">Webhook URL</span>
              <span className="settings-value" style={{ fontSize: '12px', wordBreak: 'break-all', color: '#6b7280' }}>
                {whatsappConfig?.webhookUrl || 'Not configured'}
              </span>
            </div>
          </>
        )}
      </div>

      <div className="settings-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 className="settings-section-title" style={{ marginBottom: 0 }}>Agent Settings</h3>
          {user?.role === 'ADMIN' && (
            <button
              onClick={() => {
                if (editingAgentSettings) {
                  setAgentSettingsForm(agentSettings);
                }
                setEditingAgentSettings(!editingAgentSettings);
              }}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                backgroundColor: editingAgentSettings ? '#6b7280' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {editingAgentSettings ? 'Cancel' : 'Edit'}
            </button>
          )}
        </div>

        {editingAgentSettings ? (
          <>
            <div className="settings-item">
              <span className="settings-label">Auto-assign conversations</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="settings-value" style={{ color: agentSettingsForm.autoAssignEnabled ? '#10b981' : '#6b7280' }}>
                  {agentSettingsForm.autoAssignEnabled ? 'Enabled' : 'Disabled'}
                </span>
                <div 
                  onClick={() => setAgentSettingsForm({...agentSettingsForm, autoAssignEnabled: !agentSettingsForm.autoAssignEnabled})}
                  style={{
                    width: '40px',
                    height: '20px',
                    backgroundColor: agentSettingsForm.autoAssignEnabled ? '#10b981' : '#d1d5db',
                    borderRadius: '10px',
                    position: 'relative',
                    transition: 'background-color 0.2s',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: agentSettingsForm.autoAssignEnabled ? '22px' : '2px',
                    transition: 'left 0.2s'
                  }} />
                </div>
              </div>
            </div>
            <div className="settings-item">
              <span className="settings-label">Working hours start</span>
              <input
                type="time"
                value={agentSettingsForm.workingHoursStart}
                onChange={(e) => setAgentSettingsForm({...agentSettingsForm, workingHoursStart: e.target.value})}
                className="form-input"
                style={{ width: '120px' }}
              />
            </div>
            <div className="settings-item">
              <span className="settings-label">Working hours end</span>
              <input
                type="time"
                value={agentSettingsForm.workingHoursEnd}
                onChange={(e) => setAgentSettingsForm({...agentSettingsForm, workingHoursEnd: e.target.value})}
                className="form-input"
                style={{ width: '120px' }}
              />
            </div>
            <div className="settings-item">
              <span className="settings-label">Response time target (minutes)</span>
              <input
                type="number"
                value={agentSettingsForm.responseTimeTarget}
                onChange={(e) => setAgentSettingsForm({...agentSettingsForm, responseTimeTarget: parseInt(e.target.value)})}
                className="form-input"
                style={{ width: '100px' }}
                min="1"
                max="60"
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button
                onClick={saveAgentSettings}
                className="assign-button"
              >
                Save Changes
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="settings-item">
              <span className="settings-label">Auto-assign conversations</span>
              <span className="settings-value" style={{ color: agentSettings?.autoAssignEnabled ? '#10b981' : '#6b7280' }}>
                {agentSettings?.autoAssignEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="settings-item">
              <span className="settings-label">Working hours</span>
              <span className="settings-value">
                {agentSettings ? `${agentSettings.workingHoursStart} - ${agentSettings.workingHoursEnd}` : 'Loading...'}
              </span>
            </div>
            <div className="settings-item">
              <span className="settings-label">Response time target</span>
              <span className="settings-value">
                &lt; {agentSettings?.responseTimeTarget || 5} minutes
              </span>
            </div>
          </>
        )}
      </div>

      <div className="settings-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 className="settings-section-title">Notifications</h3>
          <button 
            onClick={testAllNotifications}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Test Notifications
          </button>
        </div>
        
        <div className="settings-item" style={{ cursor: 'pointer' }} onClick={() => toggleNotification('newMessageAlerts')}>
          <span className="settings-label">New message alerts</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="settings-value" style={{ color: notifications.newMessageAlerts ? '#10b981' : '#6b7280' }}>
              {notifications.newMessageAlerts ? 'Enabled' : 'Disabled'}
            </span>
            <div 
              style={{
                width: '40px',
                height: '20px',
                backgroundColor: notifications.newMessageAlerts ? '#10b981' : '#d1d5db',
                borderRadius: '10px',
                position: 'relative',
                transition: 'background-color 0.2s'
              }}
            >
              <div 
                style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: notifications.newMessageAlerts ? '22px' : '2px',
                  transition: 'left 0.2s'
                }}
              />
            </div>
          </div>
        </div>

        <div className="settings-item" style={{ cursor: 'pointer' }} onClick={() => toggleNotification('soundEnabled')}>
          <span className="settings-label">Sound notifications</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="settings-value" style={{ color: notifications.soundEnabled ? '#10b981' : '#6b7280' }}>
              {notifications.soundEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <div 
              style={{
                width: '40px',
                height: '20px',
                backgroundColor: notifications.soundEnabled ? '#10b981' : '#d1d5db',
                borderRadius: '10px',
                position: 'relative',
                transition: 'background-color 0.2s'
              }}
            >
              <div 
                style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: notifications.soundEnabled ? '22px' : '2px',
                  transition: 'left 0.2s'
                }}
              />
            </div>
          </div>
        </div>

        <div className="settings-item" style={{ cursor: 'pointer' }} onClick={() => toggleNotification('desktopNotifications')}>
          <span className="settings-label">Desktop notifications</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="settings-value" style={{ color: notifications.desktopNotifications ? '#10b981' : '#6b7280' }}>
              {notifications.desktopNotifications ? 'Enabled' : 'Disabled'}
              {desktopPermission === 'denied' && ' (Blocked by browser)'}
              {desktopPermission === 'default' && ' (Click to enable)'}
            </span>
            <div 
              style={{
                width: '40px',
                height: '20px',
                backgroundColor: notifications.desktopNotifications ? '#10b981' : '#d1d5db',
                borderRadius: '10px',
                position: 'relative',
                transition: 'background-color 0.2s'
              }}
            >
              <div 
                style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: notifications.desktopNotifications ? '22px' : '2px',
                  transition: 'left 0.2s'
                }}
              />
            </div>
          </div>
        </div>

        <div className="settings-item" style={{ cursor: 'pointer' }} onClick={() => toggleNotification('emailNotifications')}>
          <span className="settings-label">Email notifications</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="settings-value" style={{ color: notifications.emailNotifications ? '#10b981' : '#6b7280' }}>
              {notifications.emailNotifications ? 'Enabled' : 'Disabled'} (Coming soon)
            </span>
            <div 
              style={{
                width: '40px',
                height: '20px',
                backgroundColor: notifications.emailNotifications ? '#10b981' : '#d1d5db',
                borderRadius: '10px',
                position: 'relative',
                transition: 'background-color 0.2s',
                opacity: 0.5
              }}
            >
              <div 
                style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '2px',
                  left: notifications.emailNotifications ? '22px' : '2px',
                  transition: 'left 0.2s'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3 className="settings-section-title">System</h3>
        <div className="settings-item">
          <span className="settings-label">Tenant ID</span>
          <span className="settings-value" style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>
            {user?.tenantId || 'N/A'}
          </span>
        </div>
        <div className="settings-item">
          <span className="settings-label">Version</span>
          <span className="settings-value">1.0.0</span>
        </div>
        <div className="settings-item">
          <span className="settings-label">Last updated</span>
          <span className="settings-value">Just now</span>
        </div>
      </div>
    </div>
  );
};

export default SettingsView;
